import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { customersApi, haloApi, integrationsApi, HaloCatalogueItem } from '../services/api';
import HaloItemPicker, { TERM_LABELS } from '../components/HaloItemPicker';
import './HaloPSA.css';

/**
 * HaloPSA mapping page - the one-time (then stable) wiring between CloudView
 * and the customers' existing Halo recurring invoices (RIs):
 *   Clients  customer <-> Halo RI targets (suggest / confirm / ignore / pin) + the
 *            three "no mapping yet" lists (CSV export)
 *   Items    CloudView price-book item <-> Halo item, once (suggested from the
 *            customers' invoices + names; accept all; auto-link every customer's lines)
 *   Lines    per customer: Halo RI lines vs CloudView items - link, or import an
 *            unmapped Halo line as a CloudView manual service
 *   Sync     recent `halo` sync runs
 * The monthly push itself lives on Invoice Prep (snapshot mode).
 */

type Tab = 'clients' | 'items' | 'lines' | 'sync';

interface Target {
  id: number; customer_id: number; customer_name: string | null; halo_ri_id: number; halo_client_id: number | null;
  halo_contract_ref: string | null; period: number | null; is_primary: boolean; status: string; match_score: number | null;
}
interface SimpleCustomer { id: number; xero_name: string; }

const PERIOD = (p: number | null) => (p === 3 ? 'Annual' : p === 2 ? 'Monthly' : p ? `Period ${p}` : '—');
const ZAR = (v: string | number | null | undefined) =>
  v == null || v === '' ? '—' : `R ${Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function HaloPSA() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'clients';
  const setTab = (t: Tab) => { const p = new URLSearchParams(params); p.set('tab', t); setParams(p, { replace: true }); };

  const [status, setStatus] = useState<any>(null);
  const [targets, setTargets] = useState<Target[]>([]);
  const [unmapped, setUnmapped] = useState<any>(null);
  const [customers, setCustomers] = useState<SimpleCustomer[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [tFilter, setTFilter] = useState<'suggested' | 'confirmed' | 'ignored' | 'all'>('suggested');
  const [search, setSearch] = useState('');

  const [listsMeta, setListsMeta] = useState<{ computed_at: string | null; computing: boolean; stale: boolean; duration_ms: number | null; error: string | null } | null>(null);
  const pollRef = useRef<number | null>(null);

  const loadLists = useCallback(async (refresh = false) => {
    try {
      const u = await haloApi.unmapped(refresh);
      setListsMeta(u._cache || null);
      if (u.customers_without_target) setUnmapped(u);
      if (u._cache?.computing) {
        if (pollRef.current == null) pollRef.current = window.setTimeout(() => { pollRef.current = null; loadLists(false); }, 3000);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to load the mapping lists');
    }
  }, []);

  // Fast parts first (status, targets, customers), then the cached lists.
  const load = useCallback(async () => {
    try {
      const [s, t, c] = await Promise.all([haloApi.status(), haloApi.targets(), customersApi.list()]);
      setStatus(s); setTargets(t); setCustomers((c as any[]).map(x => ({ id: x.id, xero_name: x.xero_name })));
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to load HaloPSA mapping');
    }
    loadLists(false);
  }, [loadLists]);
  useEffect(() => { load(); return () => { if (pollRef.current != null) window.clearTimeout(pollRef.current); }; }, [load]);

  const act = async (key: string, fn: () => Promise<any>, ok?: string) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(ok); await load(); return r; }
    catch (e: any) { toast.error(e.response?.data?.detail || 'Request failed'); }
    finally { setBusy(null); }
  };

  // Fast path for target edits: update the row in place, no page reload. The
  // server refreshes the lists in the background (debounced); we re-poll them
  // a few seconds after the last edit.
  const listsTimer = useRef<number | null>(null);
  const bumpLists = useCallback(() => {
    if (listsTimer.current != null) window.clearTimeout(listsTimer.current);
    listsTimer.current = window.setTimeout(() => { listsTimer.current = null; loadLists(false); }, 6000);
  }, [loadLists]);
  const applyTarget = (t: Target) => {
    setTargets(prev => {
      const next = prev.map(x => (x.id === t.id ? t : x));
      const counts: Record<string, number> = { suggested: 0, confirmed: 0, ignored: 0 };
      next.forEach(x => { counts[x.status] = (counts[x.status] || 0) + 1; });
      setStatus((s: any) => (s ? { ...s, targets: counts } : s));
      return next;
    });
    bumpLists();
  };
  const editTarget = async (t: Target, data: { status: string; customer_id?: number; is_primary?: boolean }) => {
    setBusy(`t${t.id}`);
    try { applyTarget(await haloApi.updateTarget(t.id, data)); }
    catch (e: any) { toast.error(e.response?.data?.detail || 'Request failed'); }
    finally { setBusy(null); }
  };
  const confirmAll = async () => {
    const eligible = targets.filter(t => t.status === 'suggested');
    const perfect = eligible.filter(t => (t.match_score ?? 0) >= 100).length;
    const raw = window.prompt(`Confirm all suggested matches at or above a score?\n\n${eligible.length} suggested, ${perfect} with a perfect score (100).\nMinimum score:`, '100');
    if (raw == null) return;
    const min = Number(raw);
    if (!Number.isFinite(min)) return;
    setBusy('confirmall');
    try {
      const r = await haloApi.confirmAll({ min_score: min });
      toast.success(`${r.confirmed} target${r.confirmed === 1 ? '' : 's'} confirmed`);
      const [s, t] = await Promise.all([haloApi.status(), haloApi.targets()]);
      setStatus(s); setTargets(t); bumpLists();
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Request failed'); }
    finally { setBusy(null); }
  };

  const syncNow = () => act('sync', async () => {
    await integrationsApi.sync('halo');
    toast('HaloPSA sync started - ~4 minutes for the whole estate', { icon: '⏳' });
  });
  const suggest = () => act('suggest', async () => {
    const r = await haloApi.suggestTargets();
    toast.success(`${r.suggested} suggested · ${r.ambiguous} need a pin · ${r.unmatched} unmatched`);
  });
  const downloadCsv = async () => {
    const blob = await haloApi.unmappedCsv();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'halo-unmapped.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const custName = (id: number | null) => customers.find(c => c.id === id)?.xero_name || (id ? `#${id}` : '—');
  const visibleTargets = useMemo(() => targets
    .filter(t => tFilter === 'all' || t.status === tFilter)
    .filter(t => !search || `${t.customer_name || ''} ${t.halo_contract_ref || ''}`.toLowerCase().includes(search.toLowerCase())),
    [targets, tFilter, search]);

  return (
    <div className="halopsa-page">
      <div className="page-header">
        <h1>HaloPSA</h1>
        <p className="page-description">
          Map each CloudView customer to its Halo recurring invoice once; the monthly push on Invoice Prep does the rest.
        </p>
      </div>

      {status && (
        <div className="hp-strip">
          <span className={`hp-pill ${status.configured ? 'ok' : 'warn'}`}>{status.configured ? '● Configured' : '○ Not configured (HALO_* env)'}</span>
          <span className="hp-pill">{status.ri_count} RIs · {status.line_count} lines cached</span>
          <span className="hp-pill">{status.targets.confirmed} confirmed · {status.targets.suggested} suggested · {status.targets.ignored} ignored</span>
          <span className={`hp-pill ${status.allowlist.mode === 'off' ? 'warn' : ''}`} title="HALO_PUSH_ALLOWLIST">
            Push: {status.allowlist.mode === 'all' ? 'all mapped customers' : status.allowlist.mode === 'list' ? `${status.allowlist.count} allow-listed` : 'disabled'}
          </span>
          {status.last_sync && (
            <span className="hp-pill" title={status.last_sync.message || ''}>
              Last sync {status.last_sync.status} {status.last_sync.finished_at ? new Date(status.last_sync.finished_at).toLocaleString() : ''}
            </span>
          )}
          <span className="hp-grow" />
          <button className="btn btn-sm btn-outline" disabled={busy === 'sync' || !status.configured} onClick={syncNow}>🔄 Sync HaloPSA now</button>
          <button className="btn btn-sm btn-primary" disabled={busy === 'suggest'} onClick={suggest}>✨ Suggest matches</button>
        </div>
      )}

      <div className="hp-tabs">
        {(['clients', 'items', 'lines', 'sync'] as Tab[]).map(t => (
          <button key={t} className={`hp-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t === 'clients' ? '🏢 Clients' : t === 'items' ? '🧩 Items' : t === 'lines' ? '📄 Lines' : '🕓 Sync'}
          </button>
        ))}
      </div>

      {tab === 'clients' && (
        <>
          <div className="hp-toolbar">
            {(['suggested', 'confirmed', 'ignored', 'all'] as const).map(f => (
              <button key={f} className={`hp-chip ${tFilter === f ? 'active' : ''}`} onClick={() => setTFilter(f)}>
                {f} {f !== 'all' && status ? `(${status.targets[f]})` : ''}
              </button>
            ))}
            <input className="hp-search" placeholder="Search customer / contract…" value={search} onChange={e => setSearch(e.target.value)} />
            {status && status.targets.suggested > 0 && (
              <button className="btn btn-sm btn-primary" disabled={busy != null} onClick={confirmAll}
                      title="Confirm every suggested match at or above a score you choose - one request, one background refresh">
                ✅ Confirm all suggested ({status.targets.suggested})
              </button>
            )}
          </div>
          <table className="hp-table">
            <thead>
              <tr><th>CloudView customer</th><th>Halo client</th><th>Contract</th><th>Cycle</th><th>Score</th><th>Primary</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {visibleTargets.length === 0 && <tr><td colSpan={8} className="hp-empty">Nothing here. Sync HaloPSA, then click <strong>Suggest matches</strong>.</td></tr>}
              {visibleTargets.map(t => (
                <tr key={t.id} className={`st-${t.status}`}>
                  <td>
                    <select value={t.customer_id} disabled={busy === `t${t.id}`}
                            onChange={e => editTarget(t, { status: 'suggested', customer_id: Number(e.target.value) })}>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.xero_name}</option>)}
                    </select>
                  </td>
                  <td>#{t.halo_client_id} <span className="hp-muted">RI {t.halo_ri_id}</span></td>
                  <td className="hp-ref" title={t.halo_contract_ref || ''}>{t.halo_contract_ref || '—'}</td>
                  <td>{PERIOD(t.period)}</td>
                  <td>{t.match_score ?? '—'}</td>
                  <td>
                    <input type="checkbox" checked={t.is_primary} disabled={busy === `t${t.id}`}
                           onChange={e => editTarget(t, { status: t.status, is_primary: e.target.checked })} />
                  </td>
                  <td><span className={`hp-status ${t.status}`}>{t.status}</span></td>
                  <td className="hp-actions">
                    {t.status !== 'confirmed' && <button className="btn btn-sm btn-primary" disabled={busy === `t${t.id}`} onClick={() => editTarget(t, { status: 'confirmed' })}>{busy === `t${t.id}` ? '…' : 'Confirm'}</button>}
                    {t.status !== 'ignored' && <button className="btn btn-sm btn-outline" disabled={busy === `t${t.id}`} onClick={() => editTarget(t, { status: 'ignored' })}>Ignore</button>}
                    {t.status === 'confirmed' && <button className="btn btn-sm btn-outline" onClick={() => { const p = new URLSearchParams(params); p.set('tab', 'lines'); p.set('customer', String(t.customer_id)); setParams(p); }}>Lines →</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="hp-lists">
            <div className="hp-lists-head">
              <h2>No mapping yet</h2>
              <span className="hp-muted">
                {listsMeta?.computing ? '⏳ recomputing in the background…'
                  : listsMeta?.computed_at ? `as of ${new Date(listsMeta.computed_at).toLocaleTimeString()}${listsMeta.duration_ms ? ` (${(listsMeta.duration_ms / 1000).toFixed(1)} s)` : ''}`
                  : 'not computed yet'}
                {listsMeta?.error && <span className="hp-warn"> · last refresh failed: {listsMeta.error}</span>}
              </span>
              <span className="hp-grow" />
              <button className="btn btn-sm btn-outline" disabled={!!listsMeta?.computing} onClick={() => loadLists(true)} title="Recompute the lists now (runs in the background)">↻ Refresh lists</button>
              <button className="btn btn-sm btn-outline" disabled={!unmapped} onClick={downloadCsv}>⬇ Download CSV</button>
            </div>
            {!unmapped && <p className="hp-muted">{listsMeta?.computing ? 'Computing the lists for the first time - this takes a few seconds and then stays cached.' : 'Loading…'}</p>}
            {unmapped && (
            <>
              <div className="hp-lists-grid">
                <section>
                  <h3>CloudView customers without a Halo RI <span className="hp-count">{unmapped.customers_without_target.length}</span></h3>
                  {unmapped.customers_without_target.length === 0 && <p className="hp-muted">Every billable customer has a confirmed RI.</p>}
                  <ul>
                    {unmapped.customers_without_target.map((c: any) => (
                      <li key={c.customer_id}>
                        <strong>{c.customer_name}</strong> <span className="hp-muted">· {c.lines} lines</span>
                        {c.suggested_ri_id ? <span className="hp-muted"> · suggestion pending (RI {c.suggested_ri_id})</span> : <span className="hp-muted"> · no RI matched - pin one on the right</span>}
                      </li>
                    ))}
                  </ul>
                </section>
                <section>
                  <h3>Halo RIs without a CloudView customer <span className="hp-count">{unmapped.ris_without_customer.length}</span></h3>
                  {unmapped.ris_without_customer.length === 0 && <p className="hp-muted">Every RI is confirmed or ignored.</p>}
                  <ul>
                    {unmapped.ris_without_customer.map((r: any) => (
                      <li key={r.halo_ri_id}>
                        <strong>{r.client_name}</strong> <span className="hp-muted">· RI {r.halo_ri_id} · {PERIOD(r.period)} · {ZAR(r.total)}{r.disabled ? ' · disabled' : ''}</span>
                        <div className="hp-pin">
                          {r.suggested_customer_id && <span className="hp-muted">suggested: {r.suggested_customer_name} ({r.match_score}) </span>}
                          <select defaultValue="" disabled={busy != null}
                                  onChange={e => e.target.value && act(`pin${r.halo_ri_id}`, () => haloApi.pinTarget(r.halo_ri_id, Number(e.target.value)), 'Pinned - confirm it in the table above')}>
                            <option value="">Pin to customer…</option>
                            {customers.map(c => <option key={c.id} value={c.id}>{c.xero_name}</option>)}
                          </select>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
                <section>
                  <h3>Confirmed customers with unlinked lines <span className="hp-count">{unmapped.customers_with_unlinked_lines.length}</span></h3>
                  {unmapped.customers_with_unlinked_lines.length === 0 && <p className="hp-muted">Every line is linked.</p>}
                  <ul>
                    {unmapped.customers_with_unlinked_lines.map((c: any) => (
                      <li key={c.customer_id}>
                        <button className="link-btn" onClick={() => { const p = new URLSearchParams(params); p.set('tab', 'lines'); p.set('customer', String(c.customer_id)); setParams(p); }}>
                          <strong>{c.customer_name}</strong>
                        </button>
                        <span className="hp-muted"> · {c.unlinked_halo_lines} Halo / {c.unlinked_cloudview_items} CloudView unlinked</span>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            </>
            )}
          </div>
        </>
      )}

      {tab === 'items' && <ItemsTab busy={busy} act={act} />}
      {tab === 'lines' && <LinesTab targets={targets.filter(t => t.status === 'confirmed')} customers={customers} busy={busy} act={act} />}
      {tab === 'sync' && <SyncTab />}
    </div>
  );
}

/** Licence term pill: Monthly / Annual / Annual, prepaid / Flexi - the same product
 *  is priced differently per term, so the term must be visible wherever items are matched. */
function TermPill({ code, label, title }: { code: string | null | undefined; label?: string; title?: string }) {
  if (!code) return null;
  return <span className={`hp-term hp-term-${code.replace(/[^a-z0-9]/g, '-')}`} title={title || 'Licence term'}>{label || TERM_LABELS[code] || code}</span>;
}

const TERM_ORDER = ['monthly', 'annual', 'annual_prepaid', 'flexi'];

function ItemsTab({ busy, act }: { busy: string | null; act: (key: string, fn: () => Promise<any>, ok?: string) => Promise<any> }) {
  const [data, setData] = useState<any>(null);
  const [catalogue, setCatalogue] = useState<HaloCatalogueItem[]>([]);
  const [filter, setFilter] = useState<'unmatched' | 'matched' | 'all'>('unmatched');
  const [termFilter, setTermFilter] = useState<string>('all');
  const [onlyUsed, setOnlyUsed] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [minScore, setMinScore] = useState('90');
  const load = useCallback(async () => {
    try {
      const [m, c] = await Promise.all([haloApi.itemMatching(), haloApi.items()]);
      setData(m); setCatalogue(c.items);
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Could not load item matching'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows: any[] = useMemo(() => (data?.items || [])
    .filter((r: any) => filter === 'all' || (filter === 'matched' ? r.halo_item_id != null : r.halo_item_id == null))
    .filter((r: any) => termFilter === 'all' || (termFilter === 'none' ? !r.term : r.term === termFilter) || (termFilter === 'conflict' && r.term_conflict))
    .filter((r: any) => !onlyUsed || r.customers > 0)
    .filter((r: any) => !q || `${r.name} ${r.service_type} ${r.halo_item_name || ''} ${r.suggested_halo_item_name || ''}`.toLowerCase().includes(q.toLowerCase())),
    [data, filter, termFilter, onlyUsed, q]);
  const termCounts: Record<string, number> = data?.summary?.terms || {};
  const termKeys = [...TERM_ORDER.filter(t => termCounts[t]), ...Object.keys(termCounts).filter(t => !TERM_ORDER.includes(t) && t !== 'none').sort()];
  const acceptable = (data?.items || []).filter((r: any) => r.halo_item_id == null && r.suggested_halo_item_id != null && (r.suggested_score || 0) >= Number(minScore || 0)).length;

  const setItem = async (r: any, haloItemId: number | null, msg: string) => {
    setEditing(null);
    await act(`i${r.price_book_item_id}`, () => haloApi.acceptItem(r.price_book_item_id, haloItemId), msg);
    load();
  };
  const acceptAll = async () => {
    const res = await act('acceptall', () => haloApi.acceptAllItems(Number(minScore || 90)));
    if (res) toast.success(`${res.accepted} item${res.accepted === 1 ? '' : 's'} mapped to their suggested Halo item`);
    load();
  };
  const autoLink = async () => {
    const res = await act('autolink', () => haloApi.autoLinkLines());
    if (res) toast.success(`${res.linked} line${res.linked === 1 ? '' : 's'} linked across ${res.customers} customer${res.customers === 1 ? '' : 's'}`, { duration: 7000 });
    load();
  };

  if (!data) return <div className="hp-empty">Loading item matching…</div>;
  const sm = data.summary;
  return (
    <>
      <div className="hp-strip">
        <span className="hp-pill">{sm.used_matched} / {sm.used_items} items in use matched</span>
        <span className={`hp-pill ${sm.unmatched ? 'warn' : 'ok'}`}>{sm.unmatched} unmatched · {sm.with_suggestion} with a suggestion</span>
        <span className={`hp-pill ${sm.halo_items_unmapped ? 'warn' : 'ok'}`}>{sm.halo_items_unmapped} Halo items on invoices with no CloudView item</span>
        {sm.term_conflicts > 0 && (
          <span className="hp-pill warn" title="CloudView items mapped to a Halo item of a different licence term - the prices differ">
            ⚠ {sm.term_conflicts} term mismatch{sm.term_conflicts === 1 ? '' : 'es'}
          </span>
        )}
        <span className="hp-grow" />
        <label className="hp-muted" title="Accept every suggestion at or above this score">min score
          <input className="hp-score" value={minScore} onChange={e => setMinScore(e.target.value)} />
        </label>
        <button className="btn btn-sm btn-primary" disabled={busy != null || acceptable === 0} onClick={acceptAll}
                title="Map every unmatched item to its suggested Halo item when the score is at or above the minimum">
          ✅ Accept {acceptable} suggestion{acceptable === 1 ? '' : 's'}
        </button>
        <button className="btn btn-sm btn-outline" disabled={busy != null} onClick={autoLink}
                title="For every customer with a confirmed recurring invoice: link the lines whose Halo item equals the CloudView item's Halo item">
          🔗 Auto-link lines for all customers
        </button>
      </div>
      <div className="hp-toolbar">
        {(['unmatched', 'matched', 'all'] as const).map(f => (
          <button key={f} className={`hp-chip ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
        ))}
        <span className="hp-sep" />
        <button className={`hp-chip ${termFilter === 'all' ? 'active' : ''}`} onClick={() => setTermFilter('all')} title="Every licence term">all terms</button>
        {termKeys.map(t => (
          <button key={t} className={`hp-chip hp-chip-term ${termFilter === t ? 'active' : ''}`} onClick={() => setTermFilter(t)}
                  title="CloudBlue licences on this term (their prices differ per term)">
            <TermPill code={t} /> {termCounts[t]}
          </button>
        ))}
        {termCounts.none > 0 && <button className={`hp-chip ${termFilter === 'none' ? 'active' : ''}`} onClick={() => setTermFilter('none')}>no term {termCounts.none}</button>}
        {sm.term_conflicts > 0 && <button className={`hp-chip warn ${termFilter === 'conflict' ? 'active' : ''}`} onClick={() => setTermFilter('conflict')}>⚠ term mismatch {sm.term_conflicts}</button>}
        <label className="hp-muted hp-check"><input type="checkbox" checked={onlyUsed} onChange={e => setOnlyUsed(e.target.checked)} /> only items customers actually have</label>
        <input className="hp-search" placeholder="Search item / Halo item…" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      <table className="hp-table compact hp-items">
        <thead>
          <tr>
            <th>CloudView item</th>
            <th title="Licence term out of CloudBlue - monthly, annual (billed monthly), annual prepaid and Flexi are priced differently">Term</th>
            <th className="num" title="Customers with this item / billing lines">Used by</th>
            <th>Halo item</th>
            <th>Suggestion</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="hp-empty">Nothing here.</td></tr>}
          {rows.map((r: any) => (
            <tr key={r.price_book_item_id} className={`${r.halo_item_id != null ? 'matched' : ''} ${r.term_conflict ? 'conflict' : ''}`}>
              <td>
                <span className={`service-pill ${r.service_type}`}>{r.service_type}</span> {r.term ? <>{r.base_name} <span className="hp-muted">({r.term_label})</span></> : r.name}
              </td>
              <td><TermPill code={r.term} label={r.term_label} /></td>
              <td className="num">{r.customers} <span className="hp-muted">/ {r.lines}</span></td>
              <td className="hp-item-cell">
                {editing === r.price_book_item_id ? (
                  <HaloItemPicker value={r.halo_item_id != null ? String(r.halo_item_id) : null} valueName={r.halo_item_name} items={catalogue} autoFocus
                                  onSelect={it => setItem(r, it.halo_item_id, `${r.name} → #${it.halo_item_id} ${it.name}`)}
                                  onClear={r.halo_item_id != null ? () => setItem(r, null, 'Halo mapping cleared') : undefined}
                                  onCancel={() => setEditing(null)} />
                ) : (
                  <span className={`hp-item-pick ${r.halo_item_id != null ? 'set' : 'unset'}`} onClick={() => setEditing(r.price_book_item_id)}
                        title="Click to search the Halo item catalogue">
                    {r.halo_item_id != null ? <>#{r.halo_item_id} · {r.halo_item_name}{r.halo_item_active === false && <span className="hp-warn"> (no longer in Halo)</span>}</> : 'pick a Halo item…'}
                  </span>
                )}
                {r.halo_item_id != null && editing !== r.price_book_item_id && (
                  <> <TermPill code={r.halo_item_term} title="Term in the Halo item's name" />
                  {r.term_conflict && <span className="hp-warn" title={`This is the ${r.term_label} licence but the Halo item is ${TERM_LABELS[r.halo_item_term] || r.halo_item_term} - the prices differ`}> ⚠ term differs</span>}
                  {r.term && !r.halo_item_term && <span className="hp-muted" title="The Halo item's name does not say which term it is priced for"> · Halo item has no term</span>}</>
                )}
              </td>
              <td>
                {r.halo_item_id == null && r.suggested_halo_item_id != null ? (
                  <span className="hp-sugg" title={r.reason}>
                    #{r.suggested_halo_item_id} · {r.suggested_halo_item_name} <TermPill code={r.suggested_term} title="Term in the suggested Halo item's name" /> <span className={`hp-score-pill ${r.suggested_score >= 90 ? 'hi' : r.suggested_score >= 75 ? 'mid' : 'lo'}`}>{r.suggested_score}</span>
                    <div className="hp-muted">{r.reason}</div>
                  </span>
                ) : r.halo_item_id == null ? <span className="hp-muted">no suggestion — pick one</span> : <span className="hp-muted">—</span>}
              </td>
              <td className="hp-actions">
                {r.halo_item_id == null && r.suggested_halo_item_id != null && (
                  <button className="btn btn-sm btn-primary" disabled={busy != null}
                          onClick={() => setItem(r, r.suggested_halo_item_id, `${r.name} → ${r.suggested_halo_item_name}`)}>Accept</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {data.halo_items_on_invoices?.length > 0 && (
        <div className="hp-lists">
          <div className="hp-lists-head"><strong>Halo items on the confirmed recurring invoices</strong> <span className="hp-muted">most used first · items with no CloudView item are the gaps the push cannot fill from CloudView (they can be imported per customer on the Lines tab)</span></div>
          <table className="hp-table compact">
            <thead><tr><th>Halo item</th><th className="num">Lines</th><th className="num">Customers</th><th>CloudView item(s)</th></tr></thead>
            <tbody>
              {data.halo_items_on_invoices.slice(0, 60).map((h: any) => (
                <tr key={h.halo_item_id} className={h.mapped_items.length ? 'matched' : ''}>
                  <td>#{h.halo_item_id} · {h.name} <TermPill code={h.term} />{h.group_name ? <span className="hp-muted"> · {h.group_name}</span> : null}</td>
                  <td className="num">{h.lines}</td>
                  <td className="num">{h.customers}</td>
                  <td>{h.mapped_items.length ? h.mapped_items.map((m: any) => <span key={m.price_book_item_id} className="hp-ref"><span className={`service-pill ${m.service_type}`}>{m.service_type}</span> {m.name}</span>) : <span className="hp-warn">none</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function LinesTab({ targets, customers, busy, act }: { targets: Target[]; customers: SimpleCustomer[]; busy: string | null;
  act: (key: string, fn: () => Promise<any>, ok?: string) => Promise<any> }) {
  const [params, setParams] = useSearchParams();
  const customerId = params.get('customer') ? Number(params.get('customer')) : null;
  const [view, setView] = useState<any>(null);
  const [pick, setPick] = useState<Record<number, number>>({});   // halo_line_id -> price_book_item_id
  const confirmedCustomers = useMemo(() => {
    const ids = Array.from(new Set(targets.map(t => t.customer_id)));
    return customers.filter(c => ids.includes(c.id)).sort((a, b) => a.xero_name.localeCompare(b.xero_name));
  }, [targets, customers]);

  const loadView = useCallback(async () => {
    if (!customerId) { setView(null); return; }
    try { setView(await haloApi.customerLines(customerId)); }
    catch (e: any) { toast.error(e.response?.data?.detail || 'Failed to load lines'); }
  }, [customerId]);
  useEffect(() => { loadView(); }, [loadView, busy]);

  const choose = (id: number | null) => { const p = new URLSearchParams(params); if (id) p.set('customer', String(id)); else p.delete('customer'); setParams(p, { replace: true }); };
  const itemsById = useMemo(() => new Map<number, any>((view?.cloudview_items || []).map((i: any) => [i.price_book_item_id, i])), [view]);
  const linkableItems = (view?.cloudview_items || []).filter((i: any) => i.price_book_item_id && i.link_status !== 'confirmed');
  const unlinkedHalo = (view?.halo_lines || []).filter((l: any) => l.link_status !== 'confirmed');

  return (
    <div className="hp-lines">
      <div className="hp-toolbar">
        <select value={customerId ?? ''} onChange={e => choose(e.target.value ? Number(e.target.value) : null)}>
          <option value="">Pick a confirmed customer…</option>
          {confirmedCustomers.map(c => <option key={c.id} value={c.id}>{c.xero_name}</option>)}
        </select>
        {view && unlinkedHalo.length > 0 && (
          <button className="btn btn-sm btn-primary" disabled={busy != null}
                  onClick={() => act('importall', () => haloApi.importLines(customerId!), `Imported ${unlinkedHalo.length} Halo line(s) as manual services`)}>
            ⬇ Import all {unlinkedHalo.length} unlinked Halo lines
          </button>
        )}
      </div>
      {!view && <p className="hp-muted">Pick a customer whose RI is confirmed. Link each Halo line to the CloudView item it bills, or import it so CloudView owns the line.</p>}
      {view && (
        <div className="hp-lines-grid">
          <section>
            <h3>Halo lines <span className="hp-muted">({view.targets.map((t: any) => `RI ${t.halo_ri_id} ${PERIOD(t.period)}`).join(' · ')})</span></h3>
            <table className="hp-table compact">
              <thead><tr><th>Item</th><th className="num">Qty</th><th className="num">Price</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {view.halo_lines.map((l: any) => (
                  <tr key={l.halo_line_id} className={`st-${l.link_status}`}>
                    <td>{l.item_name} <span className="hp-muted">#{l.halo_item_id}</span></td>
                    <td className="num">{l.qty_order}</td>
                    <td className="num">{ZAR(l.unit_price)}</td>
                    <td>
                      <span className={`hp-status ${l.link_status}`}>{l.link_status}</span>
                      {l.link_status === 'confirmed' && itemsById.get(l.linked_item_id) && <div className="hp-muted">→ {itemsById.get(l.linked_item_id).name}</div>}
                    </td>
                    <td className="hp-actions">
                      {l.link_status === 'confirmed' && l.linked_item_id && (
                        <button className="btn btn-sm btn-outline" disabled={busy != null}
                                title="Revoke this link - the Halo line becomes unlinked again (the CloudView line stays)"
                                onClick={() => act(`ul${l.halo_line_id}`, () => haloApi.unlinkLine(customerId!, l.linked_item_id), 'Link removed')}>
                          Unlink
                        </button>
                      )}
                      {l.link_status !== 'confirmed' && (
                        <>
                          <select value={pick[l.halo_line_id] ?? l.suggested_item_id ?? ''} disabled={busy != null}
                                  onChange={e => setPick({ ...pick, [l.halo_line_id]: Number(e.target.value) })}>
                            <option value="">Link to…</option>
                            {linkableItems.map((i: any) => <option key={i.price_book_item_id} value={i.price_book_item_id}>{i.name} ({i.quantity} @ {i.sell})</option>)}
                          </select>
                          <button className="btn btn-sm btn-primary" disabled={busy != null || !(pick[l.halo_line_id] ?? l.suggested_item_id)}
                                  onClick={() => act(`l${l.halo_line_id}`, () => haloApi.linkLine(customerId!, pick[l.halo_line_id] ?? l.suggested_item_id, l.halo_line_id), 'Linked')}>
                            Link{l.suggested_score ? ` (${l.suggested_score})` : ''}
                          </button>
                          <button className="btn btn-sm btn-outline" disabled={busy != null}
                                  onClick={() => act(`imp${l.halo_line_id}`, () => haloApi.importLines(customerId!, [l.halo_line_id]), 'Imported as a manual service')}>
                            Import
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {view.halo_lines.length === 0 && <tr><td colSpan={5} className="hp-empty">No lines cached for this RI.</td></tr>}
              </tbody>
            </table>
          </section>
          <section>
            <h3>CloudView items <span className="hp-muted">(live, grouped per price-book item)</span></h3>
            <table className="hp-table compact">
              <thead><tr><th>Item</th><th>Source</th><th className="num">Qty</th><th className="num">Sell</th><th>Status</th></tr></thead>
              <tbody>
                {view.cloudview_items.map((i: any) => (
                  <tr key={`${i.service_type}:${i.item_key}`} className={`st-${i.link_status}`}>
                    <td>{i.name}{i.halo_item_id ? <span className="hp-muted"> · Halo item #{i.halo_item_id}</span> : <span className="hp-warn"> · no Halo item on the price book</span>}</td>
                    <td>{i.service_type}</td>
                    <td className="num">{i.quantity}</td>
                    <td className="num">{ZAR(i.sell)}</td>
                    <td className="hp-actions">
                      <span className={`hp-status ${i.link_status}`}>{i.link_status === 'confirmed' ? 'linked' : i.halo_item_id ? 'will be added on push' : 'blocked'}</span>
                      {i.link_status === 'confirmed' && i.price_book_item_id && (
                        <button className="link-btn" disabled={busy != null} title="Revoke this link"
                                onClick={() => act(`ui${i.price_book_item_id}`, () => haloApi.unlinkLine(customerId!, i.price_book_item_id), 'Link removed')}>
                          unlink
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {view.cloudview_items.length === 0 && <tr><td colSpan={5} className="hp-empty">No billable lines.</td></tr>}
              </tbody>
            </table>
          </section>
        </div>
      )}
    </div>
  );
}

function SyncTab() {
  const [logs, setLogs] = useState<any[]>([]);
  useEffect(() => { integrationsApi.getLogs('halo', 25).then(setLogs).catch(() => setLogs([])); }, []);
  return (
    <table className="hp-table">
      <thead><tr><th>Started</th><th>Trigger</th><th>Status</th><th className="num">RIs</th><th>Message</th></tr></thead>
      <tbody>
        {logs.length === 0 && <tr><td colSpan={5} className="hp-empty">No HaloPSA syncs yet.</td></tr>}
        {logs.map(l => (
          <tr key={l.id}>
            <td>{l.started_at ? new Date(l.started_at).toLocaleString() : '—'}</td>
            <td>{l.trigger}</td>
            <td><span className={`hp-status ${l.status}`}>{l.status}</span></td>
            <td className="num">{l.items_synced ?? '—'}</td>
            <td className="hp-ref">{l.message}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default HaloPSA;
